import React, { useState } from 'react';
import { Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { dataSourceService } from '../../services/dataSourceService';
import { DataSourceType } from '../../types/dataSource';

interface DataSourceFormProps {
  /** Pre-select source type (e.g., when adding from a specific node) */
  defaultType?: DataSourceType;
  /** Only show these source types in the dropdown */
  typeFilter?: DataSourceType[];
  /** Callback on successful creation */
  onCreated?: () => void;
  /** Callback to cancel/close the form */
  onCancel?: () => void;
}

const TYPE_OPTIONS: { value: DataSourceType; label: string }[] = [
  { value: 's3', label: 'AWS S3' },
  { value: 'gcp', label: 'GCP Cloud Storage' },
  { value: 'postgresql', label: 'PostgreSQL' },
  { value: 'mysql', label: 'MySQL' },
  { value: 'sqlserver', label: 'SQL Server' },
];

export const DataSourceForm: React.FC<DataSourceFormProps> = ({
  defaultType,
  typeFilter,
  onCreated,
  onCancel,
}) => {
  const [type, setType] = useState<DataSourceType>(defaultType || 's3');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Cloud fields
  const [bucketName, setBucketName] = useState('');
  const [prefix, setPrefix] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [region, setRegion] = useState('us-east-1');

  // SQL fields
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [database, setDatabase] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const isCloud = type === 's3' || type === 'gcp';

  const canSubmit = name.trim() && (
    isCloud
      ? bucketName.trim() && accessKey.trim() && secretKey.trim()
      : host.trim() && database.trim() && username.trim() && password.trim()
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      await dataSourceService.createSource({
        name: name.trim(),
        type,
        config: isCloud
          ? { bucket_name: bucketName, prefix, ...(type === 's3' ? { region } : {}) }
          : { host, port: port ? parseInt(port) : undefined, database, username },
        credentials: isCloud
          ? { access_key: accessKey, secret_key: secretKey }
          : { username, password },
      });
      setSuccess(true);
      onCreated?.();
    } catch (e: any) {
      setError(e.message || 'Failed to create data source');
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 py-4 justify-center">
        <CheckCircle className="h-4 w-4" />
        Data source created successfully
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="text-xs font-medium">Name</label>
          <Input
            className="h-8 text-sm"
            placeholder="My S3 Bucket"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">Type</label>
          <Select value={type} onValueChange={(v) => setType(v as DataSourceType)}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS
                .filter((t) => !typeFilter || typeFilter.includes(t.value))
                .map((t) => (
                <SelectItem key={t.value} value={t.value} className="text-sm">
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isCloud ? (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-xs font-medium">Bucket Name</label>
              <Input
                className="h-8 text-sm"
                placeholder="my-bucket"
                value={bucketName}
                onChange={(e) => setBucketName(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Prefix (optional)</label>
              <Input
                className="h-8 text-sm"
                placeholder="data/"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
              />
            </div>
          </div>
          {type === 's3' && (
            <div className="space-y-1">
              <label className="text-xs font-medium">Region</label>
              <Input
                className="h-8 text-sm"
                placeholder="us-east-1"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-xs font-medium">
                {type === 's3' ? 'Access Key ID' : 'HMAC Access Key'}
              </label>
              <Input
                className="h-8 text-sm"
                placeholder="AKIA..."
                value={accessKey}
                onChange={(e) => setAccessKey(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">
                {type === 's3' ? 'Secret Access Key' : 'HMAC Secret'}
              </label>
              <Input
                className="h-8 text-sm"
                type="password"
                value={secretKey}
                onChange={(e) => setSecretKey(e.target.value)}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2 space-y-1">
              <label className="text-xs font-medium">Host</label>
              <Input
                className="h-8 text-sm"
                placeholder="db.example.com"
                value={host}
                onChange={(e) => setHost(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Port</label>
              <Input
                className="h-8 text-sm"
                placeholder={type === 'postgresql' ? '5432' : type === 'mysql' ? '3306' : '1433'}
                value={port}
                onChange={(e) => setPort(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Database</label>
            <Input
              className="h-8 text-sm"
              placeholder="mydb"
              value={database}
              onChange={(e) => setDatabase(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-xs font-medium">Username</label>
              <Input
                className="h-8 text-sm"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Password</label>
              <Input
                className="h-8 text-sm"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex items-center gap-2 justify-end">
        {onCancel && (
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!canSubmit || saving}
        >
          {saving ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
              Testing & Saving...
            </>
          ) : (
            'Add Source'
          )}
        </Button>
      </div>
    </div>
  );
};
